import './styles.scoped.css'
import {useEffect, useState} from 'react'
import {Typography, Button, TextField, Link, Avatar, IconButton} from '@mui/material'
import {ArrowBack, UploadFile, Upload, Edit, Delete} from '@mui/icons-material'
import {useAuth} from '../../contexts/AuthContext'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {accountApi} from '../../services'
import {useDropzone} from 'react-dropzone'
import {phoneRegex} from '../../utils/helpers'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {useNavigate, Link as LinkRoute} from 'react-router-dom'
import {useMobile} from '../../hooks'

export default () => {
    const {setAccount, user} = useAuth()
    const {setLoading, setMessageBox} = useGlobalStates()
    const {mobile} = useMobile()
    const navigate = useNavigate()
    const form = useForm({
        resolver: yupResolver(
            yup.object().shape({
                first_name: yup.string(),
                last_name: yup.string(),
                phone: yup.string().test('phone-optional', 'Invalid phone number', value => {
                    if (!value || value.trim() === '') return true
                    return phoneRegex.test(value)
                }),
            }),
        ),
    })
    const [file, setFile] = useState(null)
    const {getRootProps, getInputProps, isDragActive, open} = useDropzone({
        // No SVG: it can carry script, and the mobile app can't render it.
        accept: {'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'], 'image/gif': ['.gif'], 'image/webp': ['.webp']},
        noClick: true,
        multiple: false,
        maxSize: 3 * 1024 * 1024,
        onDrop: acceptedFiles => {
            setFile(acceptedFiles[0])
            setAvatarUrl(URL.createObjectURL(acceptedFiles[0]))
        },
    })
    const [avatarUrl, setAvatarUrl] = useState(null)

    useEffect(() => {
        Object.keys(form.getValues()).forEach(field => {
            form.setValue(field, user?.[field])
        })
    }, [user])

    useEffect(() => {
        setAvatarUrl(user?.avatar_url || null)
    }, [user])

    useEffect(() => {
        if (!file) return
        const formData = new FormData()
        formData.append('file', file)
        setLoading(true)
        accountApi
            .uploadAvatar(formData)
            .then(({data: {data}}) => {
                setLoading(false)
                setAccount(data)
            })
            .catch(() => {
                setLoading(false)
                setFile(null)
                setAvatarUrl(user?.avatar_url || null)
                setMessageBox({
                    status: 'error',
                    title: 'Photo Upload Failed',
                    description: 'There was an error uploading your photo. Please try again later.',
                })
            })
    }, [file])

    const removeFile = async () => {
        setLoading(true)
        accountApi
            .removeAvatar()
            .then(({data: {data}}) => {
                setLoading(false)
                setFile(null)
                setAccount(data)
            })
            .catch(() => {
                setLoading(false)
                setMessageBox({
                    status: 'error',
                    title: 'Photo Removal Failed',
                    description: 'There was an error removing your photo. Please try again later.',
                })
            })
    }

    const saveAccount = data => {
        setLoading(true)
        accountApi
            .updateAccount(data)
            .then(({data: {data}}) => {
                setLoading(false)
                setAccount(data)
                setMessageBox({
                    status: 'success',
                    title: 'Profile Updated Successfully',
                    description: 'Your profile has been updated successfully.',
                })
                navigate('/account-settings')
            })
            .catch(() => {
                setLoading(false)
                setMessageBox({
                    status: 'error',
                    title: 'Profile Update Failed',
                    description: 'There was an error updating your profile. Please try again later.',
                })
            })
    }

    return (
        <>
            {mobile ? (
                <>
                    <div className="header">
                        <LinkRoute to="/account-settings">
                            <IconButton>
                                <ArrowBack />
                            </IconButton>
                        </LinkRoute>
                        <Typography variant="h6" color="text.primary">
                            Edit Profile
                        </Typography>
                    </div>
                    <div className="actions">
                        <Button variant="outlined" onClick={() => navigate('/account-settings')} style={{flex: 1}}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={form.handleSubmit(saveAccount)} style={{flex: 1}}>
                            Save Changes
                        </Button>
                    </div>
                    <br />
                </>
            ) : (
                <>
                    <Typography variant="body1" color="text.secondary">
                        Settings /{' '}
                        <LinkRoute className="link" to="/account-settings">
                            Profile
                        </LinkRoute>{' '}
                        / Edit Profile
                    </Typography>
                    <br />
                    <div className="header">
                        <div className="back-container">
                            <LinkRoute className="back-button" to="/account-settings">
                                <ArrowBack style={{color: '#ffffff'}} fontSize="small" />
                            </LinkRoute>
                            <Typography variant="h5" color="text.primary">
                                Edit Profile
                            </Typography>
                        </div>
                        <div className="actions">
                            <Button variant="outlined" onClick={() => navigate('/account-settings')}>
                                Cancel
                            </Button>
                            <Button variant="contained" onClick={form.handleSubmit(saveAccount)}>
                                Save Changes
                            </Button>
                        </div>
                    </div>
                </>
            )}
            <br />
            <br />
            <Typography variant="inputLabel" color="text.secondary">
                Profile Picture
            </Typography>
            <div style={{display: avatarUrl ? 'flex' : 'none', flexDirection: 'row', alignItems: 'center', gap: 8}}>
                <Avatar src={avatarUrl} style={{height: 130, width: 130}} />
                <Button variant="outlined" startIcon={<Edit />} onClick={open}>
                    Replace
                </Button>
                <Button variant="outlined" startIcon={<Delete />} color="error" onClick={removeFile}>
                    Remove
                </Button>
            </div>
            <div
                {...getRootProps()}
                style={{
                    width: '50%',
                    backgroundColor: isDragActive ? '#e3f2fd' : '#fbfbfb',
                    display: avatarUrl || mobile ? 'none' : 'block',
                    borderColor: isDragActive ? '#3f51b5' : '#0000001F',
                    border: '1px dashed #0000001F',
                    borderRadius: 1,
                    height: 150,
                    minWidth: 300,
                    boxSizing: 'border-box',
                    textAlign: 'center',
                }}>
                <input {...getInputProps()} />
                <br />
                <UploadFile style={{fontSize: 26}} color="primary" />
                <Typography variant="body1" color="text.primary" style={{marginTop: 10, marginBottom: 10}}>
                    <Link underline="always" onClick={open} style={{cursor: 'pointer'}}>
                        {'Click to upload '}
                    </Link>
                    or drag and drop
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    PNG, JPG, GIF or WebP (max. 3MB)
                </Typography>
            </div>
            {!avatarUrl && mobile && (
                <>
                    <br />
                    <Button variant="outlined" startIcon={<Upload />} onClick={open}>
                        Upload Photo
                    </Button>
                    <br />
                </>
            )}
            <br />
            <div className="form">
                <div className="form-left">
                    <Typography variant="inputLabel" color="text.secondary">
                        First Name
                    </Typography>
                    <TextField placeholder="Enter your first name" fullWidth {...form.register('first_name')} />
                </div>
                <div className="form-right">
                    <Typography variant="inputLabel" color="text.secondary">
                        Last Name
                    </Typography>
                    <TextField placeholder="Enter your last name" fullWidth {...form.register('last_name')} />
                </div>
            </div>
            <br />
            <br />
            <Typography variant="inputLabel" color="text.secondary">
                Healthcare Phone Number
            </Typography>
            <TextField
                placeholder="Enter your healthcare phone number"
                fullWidth
                {...form.register('phone')}
                error={!!form.formState.errors['phone']}
                helperText={form.formState.errors['phone']?.message}
            />
            <br />
            <br />
            <Typography variant="inputLabel" color="text.secondary">
                Email
            </Typography>
            <TextField value={user?.email} fullWidth disabled />
        </>
    )
}
